import { useNavigate } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Drawer, EmptyState } from '../../ui';
import { useNotifications } from '../../hooks/useNotifications';
import { notificationsService } from '../../services/notifications.service';

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) {
    return 'agora';
  }
  const min = Math.floor(ms / 60000);
  if (min < 1) {
    return 'agora';
  }
  if (min < 60) {
    return `${min}m`;
  }
  const h = Math.floor(min / 60);
  if (h < 24) {
    return `${h}h`;
  }
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export function NotificationCenter({ open, onClose }: NotificationCenterProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useNotifications();
  const items = data?.items ?? [];
  const hasUnread = items.some((n) => !n.readAt);

  async function handleClick(id: number, link: string | null) {
    try {
      await notificationsService.markRead(id);
    } finally {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    }
    if (link) {
      onClose();
      navigate(link);
    }
  }

  async function handleMarkAll() {
    try {
      await notificationsService.markAllRead();
    } finally {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Notificações"
      footer={
        hasUnread ? (
          <button
            type="button"
            onClick={handleMarkAll}
            className="text-sm text-ink-secondary hover:text-ink-primary flex items-center gap-1"
          >
            <Check className="w-4 h-4" /> Marcar todas como lidas
          </button>
        ) : undefined
      }
    >
      {items.length === 0 ? (
        <EmptyState
          illustration={<Bell className="w-12 h-12" />}
          title="Sem notificações"
          body="Você está em dia."
        />
      ) : (
        <ul className="flex flex-col gap-1">
          {items.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => handleClick(n.id, n.link)}
                className={`w-full text-left p-3 rounded-md hover:bg-subtle ${
                  n.readAt ? '' : 'bg-subtle/50'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-medium text-ink-primary">{n.title}</span>
                  <span className="text-xs text-ink-tertiary shrink-0">{timeAgo(n.createdAt)}</span>
                </div>
                {n.body && <p className="text-xs text-ink-secondary mt-1">{n.body}</p>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}
